(() => {
  const STORAGE_KEY = 'asma:return-position:v3';
  const MAX_AGE = 2 * 60 * 60 * 1000;
  const RETRY_MS = 180;
  const MAX_ATTEMPTS = 90;

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  const currentKey = () => window.location.pathname + window.location.search;

  const normalizeProductPath = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return '';
      return url.pathname.replace(/\/+$/, '');
    } catch (_) {
      return '';
    }
  };

  const readState = () => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const state = JSON.parse(raw);
      if (!state || !state.source || !state.target || Date.now() - state.time > MAX_AGE) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return state;
    } catch (_) {
      return null;
    }
  };

  const saveReturnPoint = (link) => {
    const target = normalizeProductPath(link.href);
    if (!target) return;

    const card = link.closest(
      '#product-grid .grid__item, .product-grid .grid__item, .card-wrapper, .product-card-wrapper, .grid__item'
    );
    if (!card) return;

    const grid = card.closest('#product-grid, .product-grid');
    let itemIndex = -1;
    if (grid) {
      const items = Array.from(grid.querySelectorAll('.grid__item'));
      itemIndex = items.indexOf(card);
    }

    const rect = card.getBoundingClientRect();

    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          source: currentKey(),
          target,
          y: Math.max(0, window.scrollY),
          viewportTop: Math.max(0, rect.top),
          itemIndex,
          time: Date.now()
        })
      );
    } catch (_) {}
  };

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link) return;
      if (!normalizeProductPath(link.href)) return;
      saveReturnPoint(link);
    },
    true
  );

  const navigationType = (() => {
    try {
      return performance.getEntriesByType('navigation')[0]?.type || '';
    } catch (_) {
      return '';
    }
  })();

  const findTargetLink = (targetPath) => {
    const links = document.querySelectorAll('a[href*="/products/"]');
    for (const link of links) {
      if (normalizeProductPath(link.href) === targetPath) return link;
    }
    return null;
  };

  const restoreToCard = (state, link) => {
    const card = link.closest(
      '#product-grid .grid__item, .product-grid .grid__item, .card-wrapper, .product-card-wrapper, .grid__item'
    ) || link;

    const desiredViewportTop = Number.isFinite(state.viewportTop)
      ? Math.min(Math.max(state.viewportTop, 90), Math.max(90, window.innerHeight * 0.55))
      : Math.max(100, window.innerHeight * 0.22);

    const absoluteTop = window.scrollY + card.getBoundingClientRect().top;
    const targetY = Math.max(0, absoluteTop - desiredViewportTop);

    window.scrollTo({ top: targetY, left: 0, behavior: 'auto' });

    requestAnimationFrame(() => {
      const correctedTop = window.scrollY + card.getBoundingClientRect().top - desiredViewportTop;
      if (Math.abs(correctedTop - window.scrollY) > 8) {
        window.scrollTo({ top: Math.max(0, correctedTop), left: 0, behavior: 'auto' });
      }
    });

    window.setTimeout(() => {
      const finalTop = window.scrollY + card.getBoundingClientRect().top - desiredViewportTop;
      if (Math.abs(finalTop - window.scrollY) > 12) {
        window.scrollTo({ top: Math.max(0, finalTop), left: 0, behavior: 'auto' });
      }
      sessionStorage.removeItem(STORAGE_KEY);
    }, 300);
  };

  const beginRestore = (force = false) => {
    const state = readState();
    if (!state || state.source !== currentKey()) return;

    const shouldRestore = force || navigationType === 'back_forward';
    if (!shouldRestore) return;

    let attempts = 0;
    let finished = false;
    let observer = null;

    const cleanup = () => {
      finished = true;
      if (observer) observer.disconnect();
    };

    const tryRestore = () => {
      if (finished) return true;

      const targetLink = findTargetLink(state.target);
      if (targetLink) {
        cleanup();
        restoreToCard(state, targetLink);
        return true;
      }

      attempts += 1;

      const grid = document.querySelector('#product-grid, .product-grid');
      const loadedItems = grid ? grid.querySelectorAll('.grid__item').length : 0;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      const maxY = Math.max(0, documentHeight - window.innerHeight);

      const needsMoreProducts =
        state.itemIndex >= 0 ? loadedItems <= state.itemIndex : state.y > maxY - 80;

      if (needsMoreProducts) {
        window.scrollTo({ top: maxY, left: 0, behavior: 'auto' });
        window.dispatchEvent(new Event('scroll'));
      } else {
        window.scrollTo({ top: Math.min(state.y, maxY), left: 0, behavior: 'auto' });
      }

      if (attempts >= MAX_ATTEMPTS) {
        cleanup();
        window.scrollTo({ top: Math.min(state.y, maxY), left: 0, behavior: 'auto' });
        sessionStorage.removeItem(STORAGE_KEY);
        return true;
      }

      return false;
    };

    const grid = document.querySelector('#product-grid, .product-grid');
    if (grid && 'MutationObserver' in window) {
      observer = new MutationObserver(() => {
        window.setTimeout(tryRestore, 20);
      });
      observer.observe(grid, { childList: true, subtree: true });
    }

    if (tryRestore()) return;

    const timer = window.setInterval(() => {
      if (finished || tryRestore()) {
        window.clearInterval(timer);
      }
    }, RETRY_MS);
  };

  document.addEventListener('DOMContentLoaded', () => {
    beginRestore(navigationType === 'back_forward');
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted || navigationType === 'back_forward') {
      window.setTimeout(() => beginRestore(true), 30);
    }
  });
})();